import { Link } from '@tanstack/react-router';
import { ArrowLeftRight, Check, EyeOff, PiggyBank } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { signedKr } from '../../lib/money';
import { trpc } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import { type InboxItem, useInboxActions } from '../inbox/useInboxActions';
import { SavingsPickerSheet } from '../savings/SavingsPickerSheet';
import { CategoryPickerSheet, readRecent } from '../transactions/CategoryPickerSheet';
import { Button } from '../ui/button';

const dayFormat = new Intl.DateTimeFormat('sv-SE', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/**
 * The daily chore, one purchase at a time: tap the suggested category (or a
 * recent one) and the next purchase slides in.
 */
export function SortCard() {
  const {
    items,
    withSuggestion,
    bulkRunning,
    categorize,
    asSavings,
    asTransfer,
    exclude,
    acceptAll,
  } = useInboxActions(50);
  const [pickerFor, setPickerFor] = useState<InboxItem | null>(null);
  // Kept while the sheet animates closed so its title doesn't flash
  const [savingsFor, setSavingsFor] = useState<InboxItem | null>(null);
  const [savingsOpen, setSavingsOpen] = useState(false);
  const groupsQuery = trpc.category.listGroups.useQuery(undefined, {
    enabled: items.length > 0,
  });
  const [recentIds] = useState(readRecent);

  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groupsQuery.data ?? []) for (const c of g.categories) map.set(c.id, c.name);
    return map;
  }, [groupsQuery.data]);

  const item = items[0];
  if (!item) return null;

  // The suggestion first, then a few recently used categories
  const chips = [
    ...(item.suggestion
      ? [{ id: item.suggestion.categoryId, label: item.suggestion.categoryName, suggested: true }]
      : []),
    ...recentIds
      .filter((id) => id !== item.suggestion?.categoryId && names.has(id))
      .slice(0, item.suggestion ? 2 : 3)
      .map((id) => ({ id, label: names.get(id) ?? '', suggested: false })),
  ];

  return (
    <Panel
      tone="warning"
      title={
        <span className="flex items-center gap-2">
          Att sortera
          <span className="rounded-full bg-warning px-2 text-xs leading-5 font-semibold text-background tabular-nums">
            {items.length}
          </span>
        </span>
      }
      action={
        <Link to="/inbox" className="font-medium text-warning hover:underline">
          Visa alla
        </Link>
      }
    >
      <div className="rounded-xl bg-card p-3.5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p
              className="line-clamp-2 text-[15px] leading-snug font-medium text-balance wrap-break-word"
              title={item.description}
            >
              {item.description}
            </p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground capitalize">
              {dayFormat.format(new Date(`${item.date}T00:00:00Z`)).replace('.', '')}
              {item.accountName ? ` · ${item.accountName}` : ''}
            </p>
          </div>
          <span
            className={cn(
              'shrink-0 font-semibold whitespace-nowrap tabular-nums',
              item.amount > 0 && 'text-positive',
            )}
          >
            {signedKr(item.amount)}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <Button
              key={chip.id}
              type="button"
              size="sm"
              variant={chip.suggested ? 'default' : 'outline'}
              className="max-w-full rounded-full"
              onClick={() => void categorize(item, chip.id, false)}
            >
              {chip.suggested && <Check />}
              <span className="truncate">{chip.label}</span>
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant={chips.length ? 'outline' : 'default'}
            className={cn('rounded-full', chips.length > 0 && 'text-muted-foreground')}
            onClick={() => setPickerFor(item)}
          >
            {chips.length ? 'Annan kategori' : 'Välj kategori'}
          </Button>
          {/* Phones: a labelled row of their own (icons alone are cryptic and
              would wrap into an orphan row). From sm up: icon-only, trailing. */}
          <div className="-mx-2.5 -mb-1.5 grid w-[calc(100%+1.25rem)] grid-cols-3 gap-1 sm:mx-0 sm:flex sm:mb-0 sm:ml-auto sm:w-auto sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground max-sm:min-w-0 max-sm:px-1 max-sm:text-[13px] sm:w-7 sm:px-0 sm:[&_svg:not([class*='size-'])]:size-4"
              onClick={() => {
                setSavingsFor(item);
                setSavingsOpen(true);
              }}
              aria-label="Markera som sparande"
              title="Markera som sparande"
            >
              <PiggyBank />
              <span className="sm:sr-only">Sparande</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground max-sm:min-w-0 max-sm:px-1 max-sm:text-[13px] sm:w-7 sm:px-0 sm:[&_svg:not([class*='size-'])]:size-4"
              onClick={() => void asTransfer(item)}
              aria-label="Överföring mellan egna konton"
              title="Överföring mellan egna konton"
            >
              <ArrowLeftRight />
              <span className="sm:sr-only">Överföring</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground max-sm:min-w-0 max-sm:px-1 max-sm:text-[13px] sm:w-7 sm:px-0 sm:[&_svg:not([class*='size-'])]:size-4"
              onClick={() => void exclude(item)}
              aria-label="Räkna inte med i budgeten"
              title="Räkna inte med i budgeten"
            >
              <EyeOff />
              <span className="sm:sr-only">Räkna inte</span>
            </Button>
          </div>
        </div>
      </div>

      {withSuggestion.length > 1 && (
        <button
          type="button"
          disabled={bulkRunning}
          onClick={() => void acceptAll()}
          className="mt-1.5 -mb-2.5 inline-flex min-h-11 items-center text-sm font-medium text-warning hover:underline disabled:opacity-50 sm:mt-3 sm:mb-0 sm:min-h-0"
        >
          Godkänn alla {withSuggestion.length} förslag
        </button>
      )}

      <SavingsPickerSheet
        open={savingsOpen}
        onOpenChange={setSavingsOpen}
        title={savingsFor?.description}
        onSelect={(pot) => {
          if (savingsFor) void asSavings(savingsFor, pot);
        }}
      />

      <CategoryPickerSheet
        open={pickerFor !== null}
        onOpenChange={(open) => !open && setPickerFor(null)}
        title={pickerFor?.description ?? 'Välj kategori'}
        value={pickerFor?.suggestion?.categoryId ?? null}
        suggestions={
          pickerFor?.suggestion
            ? [
                {
                  categoryId: pickerFor.suggestion.categoryId,
                  label: pickerFor.suggestion.categoryName,
                },
              ]
            : []
        }
        // Empty pattern = no rule offered (incoming Swish is sorted by hand)
        showCreateRule={(pickerFor?.rulePattern.length ?? 0) > 0}
        onSelect={(categoryId, { createRule }) => {
          if (pickerFor) void categorize(pickerFor, categoryId, createRule);
        }}
      />
    </Panel>
  );
}
